import { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw, Trash2, Copy, Mail, Clock, Users, Activity } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('masterToken');
  return { Authorization: `Bearer ${token}` };
};

const STAGE_COLORS = {
  network: { bg: 'bg-red-500/15', border: 'border-red-500/30', text: 'text-red-300' },
  validation: { bg: 'bg-amber-500/15', border: 'border-amber-500/30', text: 'text-amber-300' },
  server: { bg: 'bg-red-600/15', border: 'border-red-600/30', text: 'text-red-300' },
  unknown: { bg: 'bg-white/5', border: 'border-white/10', text: 'text-[#9CA3AF]' },
};

export default function MasterSignupErrors() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/master/signup-errors`, { headers: authHeaders() });
      if (res.ok) setData(await res.json());
    } catch {
      toast.error('Failed to load signup errors');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const clearAll = async () => {
    if (!window.confirm('Delete ALL signup error records? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API}/master/signup-errors`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) {
        toast.success('All signup errors cleared');
        load();
      } else {
        toast.error('Delete failed');
      }
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="text-[#9CA3AF] p-12 text-center">Loading signup errors…</div>;

  const errors = data?.errors || [];
  const filtered = filter === 'all' ? errors : errors.filter((r) => r.role === filter);

  return (
    <div className="space-y-6" data-testid="master-signup-errors">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-syne text-2xl font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" /> Signup Errors
          </h1>
          <p className="text-sm text-[#9CA3AF] mt-1">Triage failed registrations — help people who couldn't get in.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-white/5 border border-white/10 text-white hover:bg-white/10">
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            onClick={clearAll}
            disabled={errors.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/25 disabled:opacity-40"
            data-testid="clear-all-signup-errors"
          >
            <Trash2 size={12} /> Clear all
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Activity} label="Total errors" value={data?.count || 0} gradient="from-amber-500/15 to-amber-500/5" border="border-amber-500/25" color="text-amber-300" />
        <StatCard icon={Users} label="Consumers" value={data?.by_role?.consumer || 0} gradient="from-purple-500/15 to-purple-500/5" border="border-purple-500/25" color="text-purple-300" />
        <StatCard icon={Users} label="Vendors" value={data?.by_role?.vendor || 0} gradient="from-blue-500/15 to-blue-500/5" border="border-blue-500/25" color="text-blue-300" />
        <StatCard icon={Clock} label="Last 24h" value={errors.filter(e => e.created_at && Date.now() - new Date(e.created_at).getTime() < 86400000).length} gradient="from-white/5 to-transparent" border="border-white/10" color="text-white" />
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        {['all', 'consumer', 'vendor'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition ${filter === f ? 'bg-[#8B5CF6]/30 text-white border border-[#8B5CF6]/50' : 'bg-white/5 text-[#9CA3AF] border border-white/10 hover:text-white'}`}
            data-testid={`filter-${f}`}
          >
            {f} {f !== 'all' && `(${data?.by_role?.[f] || 0})`}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="p-16 text-center rounded-2xl border border-white/10 bg-white/[0.02]">
          <AlertTriangle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <p className="text-white font-semibold mb-1">No failed signups 🎉</p>
          <p className="text-[#9CA3AF] text-sm">Either no errors reported, or you've cleared them all.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 overflow-hidden bg-white/[0.02]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white/[0.04] text-[#9CA3AF] text-[10px] uppercase tracking-wider">
                <th className="text-left font-bold p-3">When</th>
                <th className="text-left font-bold p-3">Email</th>
                <th className="text-left font-bold p-3 hidden sm:table-cell">Role</th>
                <th className="text-left font-bold p-3 hidden md:table-cell">Stage</th>
                <th className="text-left font-bold p-3">Error</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const stage = r.error_stage || 'unknown';
                const colors = STAGE_COLORS[stage] || STAGE_COLORS.unknown;
                return (
                  <tr key={i} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`err-row-${i}`}>
                    <td className="p-3 text-[#9CA3AF] text-xs whitespace-nowrap">
                      {r.created_at ? new Date(r.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[#E5E7EB] text-xs font-mono truncate max-w-[180px]">{r.email || '—'}</span>
                        {r.email && (
                          <button
                            onClick={() => { navigator.clipboard.writeText(r.email); toast.success('Email copied'); }}
                            className="text-[#64748B] hover:text-white"
                            title="Copy email"
                          >
                            <Copy size={10} />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="p-3 hidden sm:table-cell">
                      <span className="text-xs px-2 py-0.5 rounded-md bg-white/5 text-[#C4B5FD] border border-white/10 capitalize">{r.role || '—'}</span>
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      <span className={`text-[10px] uppercase px-2 py-0.5 rounded-md font-bold ${colors.bg} ${colors.text} border ${colors.border}`}>
                        {stage}
                      </span>
                    </td>
                    <td className="p-3 text-[#E5E7EB] text-xs">
                      <p className="truncate max-w-[280px]">{r.error_message || '—'}</p>
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => setSelected(r)}
                          className="text-[10px] px-2 py-1 rounded-md text-[#A78BFA] hover:text-white hover:bg-white/10 font-semibold"
                          data-testid={`err-details-${i}`}
                        >
                          Details
                        </button>
                        {r.email && (
                          <a
                            href={`mailto:${r.email}?subject=Having%20trouble%20signing%20up%20on%20EntrepreNatives%3F&body=Hi%2C%20we%20noticed%20you%20tried%20to%20sign%20up%20on%20EntrepreNatives%20but%20ran%20into%20an%20issue.%20Can%20we%20help%3F`}
                            className="text-[10px] px-2 py-1 rounded-md text-emerald-300 hover:text-white hover:bg-emerald-500/20 font-semibold inline-flex items-center gap-1"
                            title="Email this person to help them"
                          >
                            <Mail size={10} /> Help
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Details drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)} data-testid="error-details-modal">
          <div className="max-w-2xl w-full bg-[#0E0E23] border border-white/10 rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-syne font-bold text-white">Signup error details</h3>
              <button onClick={() => setSelected(null)} className="text-[#9CA3AF] hover:text-white">×</button>
            </div>
            <div className="p-5 space-y-3 text-sm max-h-[70vh] overflow-y-auto">
              {[
                ['Email', selected.email],
                ['Role', selected.role],
                ['Stage', selected.error_stage],
                ['Timestamp', selected.created_at],
                ['Error message', selected.error_message],
                ['URL', selected.url],
                ['User agent', selected.user_agent],
              ].map(([k, v]) => (
                <div key={k} className="flex items-start gap-3">
                  <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-bold w-24 shrink-0 pt-0.5">{k}</span>
                  <span className="text-[#E5E7EB] text-xs font-mono break-all">{v || '—'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, gradient, border, color }) {
  return (
    <div className={`p-4 rounded-2xl bg-gradient-to-br ${gradient} border ${border}`}>
      <Icon size={14} className={`${color} mb-2`} />
      <p className="font-mono text-xl font-bold text-white">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mt-1">{label}</p>
    </div>
  );
}
